import { useState, useEffect, useCallback, useMemo } from 'react';

const MAX_NOTIFICATIONS = 50;
let nextId = 1;

/**
 * Hook for accumulating in-draft notifications from socket events.
 * Keeps an in-memory list (not persisted) of draft events for the bell dropdown.
 */
export const useDraftNotifications = (socket, myTeamId) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((type, message) => {
    setNotifications(prev => {
      const entry = { id: nextId++, type, message, timestamp: new Date().toISOString(), read: false };
      const updated = [entry, ...prev];
      return updated.length > MAX_NOTIFICATIONS ? updated.slice(0, MAX_NOTIFICATIONS) : updated;
    });
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onPickMade = (data) => {
      // Only notify for other teams' picks
      if (data.team_id === myTeamId) return;
      const playerName = data.player?.info?.full_name || 'A player';
      const teamName = data.team_name || 'a team';
      addNotification('pick-made', `${playerName} drafted by ${teamName} (R${data.round}, Pick ${data.pick_number})`);
    };

    const onYourTurn = (data) => {
      if (data.team_id === myTeamId) {
        addNotification('your-turn', "It's your turn to pick!");
      }
    };

    const onTradeProposalReceived = (data) => {
      const fromTeam = data.from_team_name || data.trade?.proposing_team_name || 'A team';
      addNotification('trade-proposal', `Trade proposal from ${fromTeam}`);
    };

    const onTradeExecuted = (data) => {
      const pickCount = data.affected_picks?.length || 0;
      addNotification('trade-executed', `Trade completed — ${pickCount} pick${pickCount !== 1 ? 's' : ''} moved`);
    };

    const onPickSkipped = (data) => {
      const teamName = data.skipped_team_name || `Pick #${data.pick_number}`;
      addNotification('pick-skipped', `${teamName}'s pick was skipped`);
    };

    const onDraftComplete = () => {
      addNotification('draft-complete', 'The draft is complete!');
    };

    socket.on('pick-made', onPickMade);
    socket.on('your-turn', onYourTurn);
    socket.on('trade-proposal-received', onTradeProposalReceived);
    socket.on('trade-executed', onTradeExecuted);
    socket.on('pick-skipped', onPickSkipped);
    socket.on('draft-complete', onDraftComplete);

    return () => {
      socket.off('pick-made', onPickMade);
      socket.off('your-turn', onYourTurn);
      socket.off('trade-proposal-received', onTradeProposalReceived);
      socket.off('trade-executed', onTradeExecuted);
      socket.off('pick-skipped', onPickSkipped);
      socket.off('draft-complete', onDraftComplete);
    };
  }, [socket, myTeamId, addNotification]);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  const markRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return { notifications, unreadCount, markRead, markAllRead, clearAll };
};
